import { getApiBase } from "./apiBase";
import { buildAuthRequestHeaders } from "./plugins/authRequestHeaders";

/** Same auth + impersonation headers as the rest of the app (for fetch). */
export function projectFieldFileAuthHeaders() {
  return buildAuthRequestHeaders();
}

export function isProjectFieldFileContentUrl(href) {
  return /\/projects\/\d+\/field-files\/\d+\/content(?:\?|#|$)/.test(String(href || ""));
}

/**
 * Always fetch via the app API base so stored absolute URLs (API_PUBLIC_URL) still hit
 * the same origin/proxy as the rest of the client.
 */
export function resolveProjectFieldFileFetchUrl(href) {
  const t = String(href || "").trim();
  const m = t.match(/\/projects\/(\d+)\/field-files\/(\d+)\/content(?:\?|#|$)/);
  if (!m) return t;
  return `${getApiBase()}/projects/${m[1]}/field-files/${m[2]}/content`;
}

function messageFromDownloadFailure(status, bodyText) {
  const raw = String(bodyText || "").trim();
  if (raw) {
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed.error === "string" && parsed.error.trim()) {
        return parsed.error.trim();
      }
    } catch {
      /* ignore non-JSON bodies */
    }
  }
  if (status === 401) return "Sign in again to open this file.";
  if (status === 403) return "You do not have access to this file.";
  if (status === 404) return "File not found.";
  return raw || "Download failed";
}

/** Parse Content-Disposition filename / filename* (RFC 5987). */
export function filenameFromContentDisposition(header) {
  const h = String(header || "");
  if (!h) return "";
  const star = h.match(/filename\*\s*=\s*(?:UTF-8''|utf-8'')([^;\s]+)/i);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].replace(/["']/g, "").trim());
    } catch {
      /* fall through */
    }
  }
  const plain = h.match(/filename\s*=\s*"((?:\\.|[^"])*)"|filename\s*=\s*([^;\s]+)/i);
  if (plain) {
    const v = (plain[1] != null ? plain[1] : plain[2] || "").replace(/\\"/g, '"').trim();
    return v.replace(/^["']|["']$/g, "");
  }
  return "";
}

function isEarthDesktopFile(filename, accept, mimeType) {
  if (/\.(kmz|kml)$/i.test(String(filename || ""))) return true;
  if (/\.(kmz|kml)\b/i.test(String(accept || ""))) return true;
  const mime = String(mimeType || "").toLowerCase();
  return (
    mime.includes("google-earth") ||
    mime === "application/vnd.google-earth.kmz" ||
    mime === "application/vnd.google-earth.kml+xml"
  );
}

function ensureEarthFilename(filename, mimeType) {
  const name = String(filename || "").trim() || "route";
  if (/\.(kmz|kml)$/i.test(name)) return name;
  const mime = String(mimeType || "").toLowerCase();
  if (mime.includes("kml") && !mime.includes("kmz")) return `${name}.kml`;
  return `${name}.kmz`;
}

/**
 * Trigger a same-origin blob download with a real filename so the OS can hand
 * the file to ArcGIS Earth / Google Earth Pro (or the user's default handler).
 */
export function triggerBrowserDownload(blob, filename) {
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = String(filename || "download").trim() || "download";
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Delay revoke so desktop apps can finish reading the temporary blob URL.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 120_000);
}

export async function downloadProjectFieldFile(url) {
  const fetchUrl = resolveProjectFieldFileFetchUrl(url);
  const res = await fetch(fetchUrl, { headers: projectFieldFileAuthHeaders(), cache: "no-store" });
  if (!res.ok) {
    const t = await res.text();
    throw new Error(messageFromDownloadFailure(res.status, t));
  }
  const blob = await res.blob();
  const filename = filenameFromContentDisposition(res.headers.get("Content-Disposition"));
  const mimeType = res.headers.get("Content-Type") || blob.type || "";
  return { blob, filename, mimeType };
}

export async function downloadProjectFieldFileBlob(url) {
  const { blob } = await downloadProjectFieldFile(url);
  return blob;
}

/**
 * Open an authenticated field-file content URL.
 * PDFs/images open as a blob in a new tab. KMZ/KML (and other Earth files)
 * download with the original filename so ArcGIS Earth / Google Earth Pro can open them.
 * Never navigates the browser to the API URL (that returns JSON without auth headers).
 */
export async function openAuthenticatedProjectFieldFile(url, { onError, filename, accept } = {}) {
  const t = String(url || "").trim();
  if (!t || !isProjectFieldFileContentUrl(t)) return false;

  const preferEarthDownload = isEarthDesktopFile(filename, accept, "");

  // Open during the click gesture so blockers allow it; fill once the blob is ready.
  // Skip for Earth files — a blank tab is the "blink" users see, and Earth needs a download.
  let preview = null;
  if (!preferEarthDownload) {
    preview = window.open("about:blank", "_blank");
    if (preview) {
      try {
        preview.document.title = "Loading file…";
        preview.document.body.innerHTML =
          '<p style="font-family:system-ui,sans-serif;padding:24px;color:#374151">Loading file…</p>';
      } catch {
        /* some browsers restrict document writes on the new window */
      }
    }
  }

  try {
    const { blob, filename: headerName, mimeType } = await downloadProjectFieldFile(t);
    // Prefer server Content-Disposition name (original upload); caller hint is for early Earth detection.
    const name = String(headerName || filename || "").trim() || "file";

    if (preferEarthDownload || isEarthDesktopFile(name, accept, mimeType)) {
      if (preview && !preview.closed) preview.close();
      triggerBrowserDownload(blob, ensureEarthFilename(name, mimeType));
      return true;
    }

    const objectUrl = URL.createObjectURL(blob);
    if (preview && !preview.closed) {
      preview.location.replace(objectUrl);
    } else {
      window.open(objectUrl, "_blank", "noopener,noreferrer");
    }
    setTimeout(() => URL.revokeObjectURL(objectUrl), 120_000);
    return true;
  } catch (err) {
    if (preview && !preview.closed) preview.close();
    const msg = err?.message || "Could not open file";
    if (typeof onError === "function") onError(msg);
    else window.alert(msg);
    return false;
  }
}
